import { createContext, useContext, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { getServices, updateServices } from "../../api/services";
import useToaster from "../../components/alert";
import Const from "../../utils/constant";

const PageContext = createContext();

export const PageProvider = ({ children }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const isEditing = id !== "add";

  const getServicesQuery = useQuery({
    queryKey: ["services"],
    queryFn: getServices,
  });

  const updateServicesMutation = useMutation({
    mutationFn: updateServices,
  });

  // Find the specific service item to edit
  const serviceData = useMemo(() => {
    const doc = getServicesQuery?.data?.data?.[0];
    if (!doc || !isEditing) return null;
    return doc.services?.find((s) => s._id === id);
  }, [getServicesQuery, id, isEditing]);

  const initialValues = useMemo(() => {
    if (!isEditing || !serviceData) {
      return {
        title: "",
        slug_url: "",
        description: "",
        subtitle: "",
        bg_img: "",
        overview: "",
        challenge: "",
        solution: "",
        results: [],
        metrics: [],
        key_features: [],
        process: [],
        keywords: "",
        is_publish: true,
        key_features_heading: "",
        process_heading: "",
        results_heading: "",
      };
    }

    return {
      title: serviceData.title || "",
      slug_url: serviceData.slug_url || "",
      description: serviceData.description || "",
      subtitle: serviceData.subtitle || "",
      bg_img: serviceData.bg_img || "",
      overview: serviceData.overview || "",
      challenge: serviceData.challenge || "",
      solution: serviceData.solution || "",
      results: serviceData.results || [],
      metrics: serviceData.metrics || [],
      key_features: serviceData.key_features || [],
      process: serviceData.process || [],
      keywords: serviceData.keywords || "",
      is_publish: serviceData.is_publish !== undefined ? serviceData.is_publish : true,
      key_features_heading: serviceData.key_features_heading || "",
      process_heading: serviceData.process_heading || "",
      results_heading: serviceData.results_heading || "",
    };
  }, [serviceData, isEditing]);

  const handleSubmit = async (values) => {
    const doc = getServicesQuery?.data?.data?.[0];
    if (!doc) {
      toaster.error(Const.MESSAGES.FAILED, "Main services document not found.");
      return;
    }

    let updatedList = [...(doc.services || [])];

    const servicePayload = {
      title: values.title || "",
      slug_url: values.slug_url || "",
      description: values.description || "",
      subtitle: values.subtitle || "",
      bg_img: values.bg_img || "",
      overview: values.overview || "",
      challenge: values.challenge || "",
      solution: values.solution || "",
      results: values.results || [],
      metrics: values.metrics || [],
      key_features: values.key_features || [],
      process: values.process || [],
      keywords: values.keywords || "",
      is_publish: values.is_publish,
      key_features_heading: values.key_features_heading || "",
      process_heading: values.process_heading || "",
      results_heading: values.results_heading || "",
    };

    if (isEditing) {
      // Find and update item
      const idx = updatedList.findIndex((s) => s._id === id);
      if (idx !== -1) {
        // Keep original _id
        servicePayload._id = id;
        updatedList[idx] = servicePayload;
      } else {
        toaster.error(Const.MESSAGES.FAILED, "Service item to edit not found.");
        return;
      }
    } else {
      // Add new item with a temporary/placeholder or let MongoDB generate it
      updatedList.push(servicePayload);
    }

    const payload = {
      id: doc._id,
      ...doc,
      services: updatedList,
    };

    updateServicesMutation.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries(["services"]);
        toaster.success(
          Const.MESSAGES.SUCCESS,
          isEditing ? "Service detail updated successfully" : "Service detail added successfully"
        );
        navigate("/services");
      },
      onError: (err) => {
        toaster.error(
          Const.MESSAGES.FAILED,
          err?.response?.data?.error || "Failed to save service details"
        );
      },
    });
  };

  return (
    <PageContext.Provider
      value={{
        isEditing,
        initialValues,
        handleSubmit,
        isLoading: getServicesQuery.isLoading,
      }}>
      {children}
    </PageContext.Provider>
  );
};

export const useAddServiceProvider = () => {
  return useContext(PageContext);
};
